import React, { useState } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import styled from "styled-components"

import Image from "../utils/Image"
import Reveal from "../utils/Reveal"

import Card from "../atoms/Card"
import Section from "../atoms/Section"
import Flex from "../atoms/Flex"
import Box from "../atoms/Box"
import { Title } from "../atoms/Typography"

import InteractiveInfoCard from "../molecules/InteractiveInfoCard"
import SectionTitle from "../molecules/SectionTitle"
import Icon from "../atoms/Icon"

export const renderIcon = ({ iconAttribute, iconStyles }) => {
  iconAttribute = iconAttribute?.image
  if (!iconAttribute) return null

  const iconSize = iconStyles?.iconSize

  return (
    iconAttribute && (
      <Icon
        flexShrink={0}
        {...iconAttribute}
        mb={4}
        width={
          iconStyles?.iconFit === "height"
            ? "auto"
            : iconStyles?.iconFit === "width" || iconSize
              ? `${iconSize}px`
              : 2
        }
        height={
          iconStyles?.iconFit === "width"
            ? "auto"
            : iconStyles?.iconFit === "height" || iconSize
              ? `${iconSize}px`
              : 2
        }
        maxWidth={
          iconStyles?.iconFit === "width" || iconStyles?.iconFit === "square"
            ? `${iconSize}px`
            : "auto"
        }
        maxHeight={
          iconStyles?.iconFit === "height" || iconStyles?.iconFit === "square"
            ? `${iconSize}px`
            : "auto"
        }
      />
    )
  )
}

const StyledBox = styled(Box)`
  transition:
    opacity 0.3s cubic-bezier(0.17, 0.67, 0.58, 0.84),
    transform 0.3s cubic-bezier(0.17, 0.67, 0.58, 0.84);
`

const StyledTitle = styled(Title)`
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`

const StyledImage = styled(Image)`
  &.gatsby-image-wrapper,
  &.gatsby-image-wrapper-constrained {
    & div {
      max-width: 100% !important;
    }
  }
`

const TitleTabs = ({
  cardTitleLocation,
  interactiveInfoCards,
  changeOnHover,
  activeIndex,
  setActiveIndex,
  textAlign,
  ...rest
}) =>
  interactiveInfoCards.map((interactiveInfoCard, i) => {
    const isCenter = textAlign === "center"
    const isRight = textAlign === "right"

    // Gap between tab titles. No gap at the outer edge of the row
    const spacing =
      interactiveInfoCards.length <= 1
        ? 0
        : isRight
          ? i === 0
            ? 0
            : { _: 4, xs: 6, md: 7 }
          : i === interactiveInfoCards.length - 1
            ? 0
            : { _: 4, xs: 6, md: 7 }

    return (
      <StyledTitle
        key={i}
        variant="h3"
        pb={{ _: 0, sm: 5, lg: 7 }}
        mb={{ _: 5, sm: 0 }}
        margin={isCenter ? { _: "0px auto", sm: "0px" } : "0px"} // center titles on small screens
        // Use pr for left/center, pl for right
        pr={!isRight ? spacing : 0}
        pl={!isCenter && isRight ? spacing : 0}
        onClick={
          !changeOnHover && cardTitleLocation === "tabs"
            ? () => setActiveIndex(i)
            : undefined
        }
        onMouseEnter={
          changeOnHover && cardTitleLocation === "tabs"
            ? () => setActiveIndex(i)
            : undefined
        }
        textDecoration={i === activeIndex ? "underline" : "none"}
        style={{ textUnderlinePosition: "under" }}
        {...rest}
      >
        {interactiveInfoCard.title}
      </StyledTitle>
    )
  })

const InteractiveInfoCards = ({
  section,
  sectionTitle,
  interactiveInfoCards,
  card,
  cardsAlign = "left",
  iconLocation = "top",
  imageLocation,
  interactiveInfoCardsImageLocation,
  imageAlign,
  interactiveInfoCardsImageAlign,
  iconSize,
  changeOnHover,
  cardTitleLocation = "onCard",
  mediaRatio,
  interactiveInfoCardsMediaRatio,
  columnsDesktop,
  columnsMobile,
  iconAttribute,
  iconStyles,
  spacing = 24,
}) => {
  if (cardTitleLocation === null) cardTitleLocation = "onCard"
  const [activeIndex, setActiveIndex] = useState(0)

  const imageAlignment = imageLocation || interactiveInfoCardsImageLocation
  const imageVertAlignment = imageAlign || interactiveInfoCardsImageAlign
  const fullWidthTab =
    cardTitleLocation === "tabs" &&
    interactiveInfoCards?.[activeIndex]?.image === null

  const ratio =
    mediaRatio === null
      ? 50 // defaulting mediaRatio 50% for layoutStyle standand or if undefined
      : mediaRatio || interactiveInfoCardsMediaRatio

  const mapAlignItems = value =>
    value === "right" ? "end" : value === "center" ? "center" : "start"
  const mapJustifyContent = value =>
    value === "right"
      ? "flex-end"
      : value === "center"
        ? { _: "space-between", xs: "center" }
        : "flex-start"

  return (
    <Section {...section}>
      {sectionTitle && (
        <Reveal>
          <Flex
            flexDirection="column"
            alignItems={mapAlignItems(sectionTitle?.align)} // Aligns sectionTitle horizontally
          >
            {renderIcon({ iconAttribute, iconStyles })}
            <SectionTitle {...sectionTitle} mb={6} maxWidth={42} />
          </Flex>
        </Reveal>
      )}

      <Box
        display="flex"
        flexDirection="row"
        justifyContent={mapJustifyContent(sectionTitle?.align)} //Aligns the tab titles row
        flexWrap="wrap"
      >
        {cardTitleLocation === "tabs" && (
          <TitleTabs
            cardTitleLocation={cardTitleLocation}
            interactiveInfoCards={interactiveInfoCards}
            changeOnHover={changeOnHover}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            textAlign={sectionTitle?.align}
          />
        )}
      </Box>

      <Flex
        justifyContent="space-between"
        width="100%"
        flexDirection={{
          _: "column",
          lg: imageAlignment === "right" ? "row-reverse" : "row",
        }}
        alignItems={
          cardTitleLocation === "tabs" && imageVertAlignment === "center"
            ? "center"
            : cardTitleLocation === "tabs" && imageVertAlignment === "bottom"
              ? "end"
              : cardTitleLocation === "tabs" && imageVertAlignment === "top"
                ? "start"
                : "stretch"
        }
        style={{ gap: "32px" }}
      >
        {!fullWidthTab && (
          <Box
            width={{ _: "100%", lg: "50%" }}
            flex={ratio / 100}
            mb={{ _: 6, lg: 0 }}
            position="relative"
            minHeight={0}
          >
            {interactiveInfoCards?.map((card, idx) => {
              return (
                <StyledBox
                  key={idx}
                  display="flex"
                  flexDirection="column"
                  justifyContent={
                    imageVertAlignment === "top"
                      ? "start"
                      : imageVertAlignment === "center"
                        ? "center"
                        : imageVertAlignment === "bottom"
                          ? "flex-end"
                          : "space-between"
                  }
                  width="100%"
                  opacity={idx === activeIndex ? 1 : 0}
                  height={idx === activeIndex ? "100%" : "0px"}
                  transform={
                    idx === activeIndex ? "translateX(0)" : "translateX(100%)"
                  }
                >
                  <StyledImage
                    imageFieldId={`${section.sectionId}.interactiveInfoCards[${idx}].image`}
                    {...card.image}
                  />
                </StyledBox>
              )
            })}
          </Box>
        )}

        <Box
          width={{ _: "100%", lg: fullWidthTab ? "100%" : "45%" }}
          flex={fullWidthTab ? 1 : (100 - ratio) / 100}
        >
          <Box
            display="flex"
            width="100%"
            flexDirection="column"
            alignItems={{
              _:
                cardsAlign === "center"
                  ? "center"
                  : cardsAlign === "right"
                    ? "end"
                    : "start",
            }}
          >
            {cardTitleLocation === "tabs" && (
              <InteractiveInfoCard
                key="active-card" /* render one card (same key) so that React doesn't remount */
                mx={cardsAlign === "center" ? "auto" : 0}
                width="100%"
                iconLocation={iconLocation}
                iconSize={iconSize}
                align={cardsAlign}
                {...interactiveInfoCards[activeIndex]}
                showTitle={cardTitleLocation !== "tabs"}
                idx={activeIndex}
                active
                onClick={
                  !changeOnHover && cardTitleLocation === "onCard"
                    ? () => setActiveIndex(activeIndex)
                    : undefined
                }
                onMouseEnter={
                  changeOnHover && cardTitleLocation === "onCard"
                    ? () => setActiveIndex(activeIndex)
                    : undefined
                }
                card={card}
              />
            )}
            {cardTitleLocation === "onCard" &&
              interactiveInfoCards.map((interactiveInfoCard, i) => (
                <InteractiveInfoCard
                  key={i}
                  mx={cardsAlign === "center" ? "auto" : 0}
                  mb={
                    i === interactiveInfoCards.length - 1 ? 0 : `${spacing}px`
                  }
                  width="100%"
                  iconLocation={iconLocation}
                  iconSize={iconSize}
                  align={cardsAlign}
                  {...interactiveInfoCard}
                  showTitle={cardTitleLocation !== "tabs"}
                  idx={i}
                  active={activeIndex === i}
                  onClick={
                    !changeOnHover && cardTitleLocation === "onCard"
                      ? () => setActiveIndex(i)
                      : undefined
                  }
                  onMouseEnter={
                    changeOnHover && cardTitleLocation === "onCard"
                      ? () => setActiveIndex(i)
                      : undefined
                  }
                  card={card}
                  columnsDesktop={columnsDesktop}
                  columnsMobile={columnsMobile}
                />
              ))}
          </Box>
        </Box>
      </Flex>
    </Section>
  )
}

export default InteractiveInfoCards

InteractiveInfoCards.propTypes = {
  strapi_id: PropTypes.number.isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  interactiveInfoCards: PropTypes.arrayOf(
    PropTypes.shape(InteractiveInfoCard.strapiProps),
  ),
  cardsAlign: PropTypes.oneOf(["left", "center", "right"]).isRequired,
  iconLocation: PropTypes.oneOf(["top", "left"]).isRequired,
  card: PropTypes.shape(Card.strapiProps).isRequired,
  image: PropTypes.object,
  imageLocation: PropTypes.oneOf(["left", "right"]),
}

export const query = graphql`
  fragment InteractiveInfoCards on STRAPI__COMPONENT_SECTIONS_INTERACTIVE_INFO_CARDS {
    strapi_id
    changeOnHover
    interactiveInfoCardsImageLocation: imageLocation
    interactiveInfoCardsImageAlign: imageAlign
    interactiveInfoCardsMediaRatio: mediaRatio
    cardTitleLocation
    INTERACTIVEINFOCARDSSection: section {
      ...Section
    }
    sectionTitle {
      ...SectionTitle
    }
    interactiveInfoCards {
      ...InteractiveInfoCard
    }
    card {
      ...Card
    }
    columnsDesktop
    columnsMobile
    iconAttribute {
      ...IconAttribute
    }
    iconStyles {
      ...IconStyles
    }
    spacing
  }
`
